import React, { useMemo } from 'react';

import { Box } from '@rbilabs/components-library';

import { IPicker } from '@rbi-ctg/menu';
import BonusPoints from 'components/bonus-points/bonus-points';
import { DeliveryPriceDisclaimer } from 'components/delivery-price-disclaimer';
import { ReadMoreAccordion } from 'components/read-more-accordion';
import BlockRenderer from 'components/sanity-block-renderer';
import { useFeatureDisclaimers } from 'hooks/use-feature-disclaimers';
import useMediaQuery, { MediaQuery } from 'hooks/use-media-query';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useProductWizardContext } from 'state/product-wizard';

import { NutritionInformation } from '../nutrition-information';
import { PriceAndCalories } from '../price-and-calories';
import { createPickerPromotionMap } from '../utils';

import {
  BackgroundImageColor,
  ImageDisclaimerText,
  OverviewContainer,
  StyledProductPicture,
  Title,
} from './product-overview.styled';

const ProductOverview: React.FC = () => {
  const { menuObject, selectedProduct, userSelections } = useProductWizardContext();

  const isDesktop = useMediaQuery(MediaQuery.Desktop);
  const { featureDisclaimers } = useFeatureDisclaimers();

  const enableProductImageDisclaimer = useFlag(LaunchDarklyFlag.ENABLE_PRODUCT_IMAGE_DISCLAIMER);

  const { images: imagesDisclaimer } = featureDisclaimers || {};

  const promotionBonusPoints = useMemo(() => {
    if (!userSelections.pickerAspects) {
      return null;
    }
    const pickerPromotionMap = createPickerPromotionMap((menuObject as IPicker).options);
    let promotion;
    for (const pickerSelectionId in userSelections.pickerAspects) {
      const pickerIdentifier = userSelections.pickerAspects[pickerSelectionId];
      promotion = pickerPromotionMap[`${pickerSelectionId}-${pickerIdentifier}`];
      if (promotion) {
        break;
      }
    }
    return promotion?.bonusPoints;
  }, [menuObject, userSelections]);

  return (
    <OverviewContainer>
      <Box flexDirection="column" position="relative">
        <BackgroundImageColor />
        <StyledProductPicture image={menuObject?.image} alt="" />
        {enableProductImageDisclaimer && (
          <ImageDisclaimerText>{imagesDisclaimer?.locale}</ImageDisclaimerText>
        )}
      </Box>
      <Box
        padding="0 1rem 1rem"
        flexDirection="column"
        width={isDesktop ? '464px' : '100%'}
        margin="0 auto"
      >
        <Box alignContent="center" align="center" alignSelf="center">
          <Title data-testid="product-title">{selectedProduct?.name?.locale ?? ''}</Title>
        </Box>
        <PriceAndCalories />
        <DeliveryPriceDisclaimer placement="product" />
        <ReadMoreAccordion>
          <BlockRenderer content={menuObject.description?.localeRaw} />
        </ReadMoreAccordion>
        {promotionBonusPoints && <BonusPoints bonusPoints={promotionBonusPoints} />}
        {isDesktop && <NutritionInformation />}
      </Box>
    </OverviewContainer>
  );
};

export default ProductOverview;
